// ---------------------------------------------------------------------------
// Saúde da fila do Firestore — a sonda e a única recuperação possível.
//
// ## O estado que isto existe para detectar
//
// A `AsyncQueue` do Firestore é estritamente serial (medido no
// `firebase@12.10.0`: `this.tail = this.tail.then(...)`). Dois desfechos a
// matam de forma permanente:
//
//  · uma operação de IndexedDB que **nunca resolve** — a cadeia inteira para,
//    e toda leitura/escrita posterior fica presa atrás dela;
//  · uma que **rejeita** — o SDK grava `this.failure`, re-lança, e a partir daí
//    `verifyNotFailed()` faz TODA chamada seguinte curto-circuitar num
//    `INTERNAL ASSERTION FAILED`.
//
// Nos dois casos o React continua montado e a tela continua com o que já tinha
// carregado. É o relato de device de 27/08: *"a tela até tem informações ainda
// mas não sai desse travamento"*, e só matar o app resolve.
//
// O gatilho medido é a suspensão do WebView no background (ver
// `firestoreCache.ts`). O `firestoreCache.ts` ataca a CAUSA mais provável; este
// arquivo é a rede de segurança para quando ela não for suficiente — porque
// hoje o app **não tem caminho de recuperação nenhum**: zero chamadas a
// `terminate`, `enableNetwork`, `disableNetwork` ou `clearIndexedDbPersistence`
// em todo o `src/`.
//
// ## Por que a recuperação é RECARREGAR, e não `terminate()`
//
// `terminate(db)` parece a saída limpa e não é: ele também enfileira o próprio
// desligamento na mesma `AsyncQueue`. Com a fila travada, **o terminate trava
// junto**. Recarregar o documento é a única operação que não passa por ela — e
// no app nativo o bundle é local (`server.hostname: localhost`), então a volta
// custa o placeholder de abertura, não uma ida à rede.
//
// É exatamente o que "fechar e abrir o app" faz hoje à mão, automático e sem
// o relaunch do sistema.
//
// O irmao desta ideia e o `lib/chunkReload.ts` (BUG-37), que recarrega por
// chunk obsoleto e chegou a MESMA conclusao sobre o contador: *"sem
// sessionStorage nao ha como contar tentativas — nao arrisca o loop"*. Os
// dois orcamentos ficam SEPARADOS de proposito: um reload por deploy antigo
// nao pode gastar a vaga que existe para destravar o Firestore.
// ---------------------------------------------------------------------------

/** Veredito da sonda. `dead` = nada do Firestore volta a responder. */
export type FirestoreQueueVerdict = "alive" | "dead";

/**
 * Teto da sonda. Uma leitura de cache saudável assenta em dezenas de ms; o
 * folgado aqui é para não confundir "IndexedDB acordando depois do background"
 * com "fila morta" — um falso positivo custa um reload.
 */
export const FIRESTORE_PROBE_TIMEOUT_MS = 5_000;

/**
 * Quantos reloads de recuperação uma abertura do app pode gastar.
 *
 * Existe porque o modo de falha oposto é pior: se o IndexedDB estiver
 * definitivamente quebrado, uma recuperação sem teto vira **loop de boot**.
 * Dois é o suficiente para cobrir a transição suja e parar antes de virar
 * ciclo; estourado o teto, o app fica travado — e travado é melhor que
 * reiniciando para sempre.
 */
export const FIRESTORE_RECOVERY_MAX_RELOADS = 2;

/** Chave do contador. `sessionStorage`: morre com o processo, que é a janela certa. */
export const FIRESTORE_RECOVERY_KEY = "livra:firestore_recovery_reloads";

/**
 * Traduz o ASSENTAMENTO da sonda em veredito.
 *
 * ⚠️ Rejeição normal é `alive`, e isso é o ponto: a sonda lê um documento que
 * não existe, então o caminho SAUDÁVEL é justamente uma rejeição
 * (`unavailable` — "Failed to get document from cache"). O que denuncia a fila
 * morta é o `INTERNAL` do `verifyNotFailed()`, não o fato de ter rejeitado.
 */
export function classifyProbeSettlement(err: unknown): FirestoreQueueVerdict {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  if (code === "internal") return "dead";

  const raw = (err as { message?: unknown } | null | undefined)?.message;
  const message = typeof raw === "string" ? raw : "";
  // O SDK 12.x reporta a fila envenenada por id numérico
  // (`fail(0xb815, …)` → "INTERNAL ASSERTION FAILED"). Casar no texto é
  // defensivo: o `code` acima já cobre o caminho tipado.
  if (/INTERNAL ASSERTION/i.test(message)) return "dead";

  return "alive";
}

/**
 * Corre a leitura contra o relógio. Não assentar É o sintoma — por isso o
 * timeout resolve `dead` em vez de rejeitar.
 *
 * `read` é injetado para o teste não precisar de um Firestore de verdade e
 * para o call site poder escolher uma leitura que **não custa nada**: ver
 * `probeFirestoreCache` no watcher.
 */
export async function probeFirestoreQueue(
  read: () => Promise<unknown>,
  timeoutMs: number = FIRESTORE_PROBE_TIMEOUT_MS,
): Promise<FirestoreQueueVerdict> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const settled: Promise<FirestoreQueueVerdict> = (async () => {
    try {
      await read();
      return "alive";
    } catch (err) {
      return classifyProbeSettlement(err);
    }
  })();

  const expired = new Promise<FirestoreQueueVerdict>((resolve) => {
    timer = setTimeout(() => resolve("dead"), timeoutMs);
  });

  try {
    return await Promise.race([settled, expired]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// O SEGUNDO modo de morte — transporte morto com a fila viva (BUG-179).
//
// O conserto de 27/08 (cache single-tab + sonda + reload) matou o modo "fila
// morta". Na 1.7 o sintoma voltou porque existe um segundo modo, que a sonda
// de CACHE não tem como ver:
//
//  · a suspensão do iOS mata a conexão do Watch stream EM SILÊNCIO — nenhum
//    erro chega ao SDK, então o stream segue marcado como aberto e saudável;
//  · TODO dado de servidor passa por esse stream (medido no `firebase@12.10.0`:
//    `remoteStoreListen` envia até o one-shot `getDocs`/`getDoc` como um listen
//    no mesmo Watch stream). Stream morto = onSnapshot para de entregar E
//    one-shot pendura para sempre;
//  · o `onlineState` só cai para `Offline` quando o stream FALHA ou no timeout
//    de 10s do start — um stream que já estava aberto nunca reinicia, então o
//    SDK segue "online" e o `getDocs` default espera o servidor em vez de cair
//    no cache;
//  · o único empurrão que reinicia os streams é o evento `online` do `window`
//    (`BrowserConnectivityMonitor`), que o WKWebView NÃO dispara ao voltar da
//    suspensão — a rede nunca "mudou" do ponto de vista do processo congelado.
//
// A sonda de cache responde `alive` (a fila ESTÁ viva) e nada cura. É o relato
// de 29/08: "o app para de carregar as informações, em todas as abas".
//
// ## O remédio é o do PRÓPRIO SDK, dado pelo lado de fora
//
// Quando o evento `online` chega, o SDK executa `restartNetwork` =
// `disableNetworkInternal` + `enableNetworkInternal`, com o comentário literal
// *"we don't have any other way to tear down our streams"*. A API pública
// `disableNetwork(db)` / `enableNetwork(db)` percorre exatamente esse caminho:
// derruba os streams zumbis, reabre, e o `onWatchStreamOpen` re-envia TODOS os
// listens registrados (`Ia.forEach(sendWatchRequest)`) com resume token — os
// `onSnapshot` do app voltam a entregar SEM re-assinar nada, e os one-shots
// pendurados assentam (cache no default, rejeição no source:"server").
//
// O ciclo roda SÓ no app nativo: na web o evento `online` existe e funciona, e
// a doutrina do BUG-177 é a web ficar byte a byte como está. A PWA no iOS
// segue exposta, como já registrado lá.
// ---------------------------------------------------------------------------

/**
 * Desfecho do ciclo de rede na retomada.
 *
 *  · `restarted` — streams derrubados e reabertos; nada mais a fazer.
 *  · `hung`      — o ciclo não assentou no teto. `disableNetwork`/`enableNetwork`
 *                  são operações LOCAIS da fila (não esperam servidor): não
 *                  assentar significa que a fila está presa atrás de algo — o
 *                  mesmo estado que a sonda existe para pegar, por outra porta.
 *  · `failed`    — o ciclo rejeitou. Não é prova de fila morta; recarregar por
 *                  isso seria falso positivo.
 */
export type FirestoreNetworkCycleOutcome = "restarted" | "hung" | "failed";

/**
 * Teto do ciclo. As duas chamadas só mexem em estado local e fecham streams —
 * assentam em ms. O folgado é para IndexedDB/rede acordando depois do
 * background não virar reload à toa.
 */
export const FIRESTORE_NETWORK_CYCLE_TIMEOUT_MS = 8_000;

/**
 * Corre o ciclo disable→enable contra o relógio. `cycle` é injetado pela mesma
 * razão do `read` da sonda: o teste não precisa de um Firestore de verdade.
 */
export async function cycleFirestoreNetwork(
  cycle: () => Promise<void>,
  timeoutMs: number = FIRESTORE_NETWORK_CYCLE_TIMEOUT_MS,
): Promise<FirestoreNetworkCycleOutcome> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const settled: Promise<FirestoreNetworkCycleOutcome> = (async () => {
    try {
      await cycle();
      return "restarted";
    } catch {
      return "failed";
    }
  })();

  const expired = new Promise<FirestoreNetworkCycleOutcome>((resolve) => {
    timer = setTimeout(() => resolve("hung"), timeoutMs);
  });

  try {
    return await Promise.race([settled, expired]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Quantos reloads de recuperação esta abertura já gastou. */
// ---------------------------------------------------------------------------
// A VERIFICAÇÃO pós-ciclo — porque "restarted" era aspiracional (BUG-196).
//
// O ciclo acima embarcou na 1.8 e o sintoma continuou (relato de device,
// 01/09: "desligo a tela, volto, e para de carregar, para de ter ações").
// Medido no `@firebase/firestore@4.12.0` instalado: o handler de enable faz
// `shouldStartWatchStream(e) ? startWatchStream(e) : …` — o start do stream é
// DISPARADO, nunca aguardado. Ou seja, `enableNetwork(db)` resolve quando a
// fila processa a ordem, não quando o stream reconecta. Quando a falha está
// ABAIXO dos streams (o pool de conexões do WKWebView morto depois da
// suspensão — o mesmo estado em que nem `<img>` carrega), o ciclo derruba o
// zumbi, dispara um start que pendura em silêncio, resolve "restarted"… e o
// watcher se dava por satisfeito. Era o ÚNICO caminho da máquina de estados
// que terminava sem cura e sem reload — e era o caminho vivo no aparelho.
//
// O remédio é o do BUG-184 (captura assentada) aplicado aqui: **verificar em
// vez de assumir**. Depois do ciclo, uma leitura que EXIGE o servidor
// (`getDocFromServer`) corre contra o relógio:
//
//  · o doc da sonda não casa com regra nenhuma nas rules (não há
//    `match /{document=**}`), então o desfecho SAUDÁVEL é o servidor negar —
//    `permission-denied` PROVA a ida e volta, e leitura negada por rules não
//    é cobrada: a verificação custa zero;
//  · `unavailable` com o aparelho ONLINE é o SDK desistindo do stream (o
//    OnlineStateTracker flipa para Offline após 10s sem resposta) — transporte
//    ainda morto;
//  · não assentar no teto é o mesmo estado, pego antes do flip.
//
// Nos dois últimos, a saída é a que comprovadamente cura: reload (com vaga).
// `navigator.onLine === false` pula a verificação — modo avião usando o app
// do cache não pode pagar 2 reloads por uma rede que não existe.
// ---------------------------------------------------------------------------

/** Desfecho da verificação de entrega pós-ciclo. */
export type FirestoreDeliveryVerdict = "delivering" | "dead" | "offline";

/**
 * Teto da verificação. Precisa ser MAIOR que os 10s do OnlineStateTracker do
 * SDK: no estado zumbi o flip para Offline rejeita a leitura com `unavailable`
 * aos ~10s, que já é veredito — o teto é só o backstop de o flip também não
 * acontecer.
 */
export const FIRESTORE_VERIFY_TIMEOUT_MS = 12_000;

/**
 * Traduz o assentamento da leitura de servidor em veredito de entrega.
 *
 * ⚠️ `permission-denied` é o caminho FELIZ (ver o bloco acima): qualquer
 * rejeição que não seja `unavailable` veio do servidor, logo o transporte
 * entrega. `unavailable` só é morte com o aparelho online — offline de
 * verdade é o SDK funcionando como desenhado.
 */
export function classifyDeliverySettlement(
  err: unknown,
  isOnline: boolean,
): FirestoreDeliveryVerdict {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  if (code === "unavailable") return isOnline ? "dead" : "offline";
  return "delivering";
}

/**
 * Corre a leitura de SERVIDOR contra o relógio. `read` e `isOnline` injetados
 * pela mesma razão da sonda: teste sem Firestore de verdade, e o call site
 * decide a leitura barata (`getDocFromServer` do doc da sonda).
 *
 * `isOnline` é lido NO DESFECHO, não na entrada: o rádio pode cair no meio da
 * janela, e classificar com a foto velha transformaria queda de rede em
 * reload.
 */
export async function verifyFirestoreDelivery(
  read: () => Promise<unknown>,
  isOnline: () => boolean,
  timeoutMs: number = FIRESTORE_VERIFY_TIMEOUT_MS,
): Promise<FirestoreDeliveryVerdict> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const settled: Promise<FirestoreDeliveryVerdict> = (async () => {
    try {
      await read();
      return "delivering";
    } catch (err) {
      return classifyDeliverySettlement(err, isOnline());
    }
  })();

  const expired = new Promise<FirestoreDeliveryVerdict>((resolve) => {
    timer = setTimeout(() => resolve(isOnline() ? "dead" : "offline"), timeoutMs);
  });

  try {
    return await Promise.race([settled, expired]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Orçamento SEPARADO do reload por amostra de TELA (BUG-197, 5ª fatia).
 *
 * O orçamento acima é DEVOLVIDO a cada `verify-ok` — certo para transporte
 * (uma retomada que prova saúde rearma o teto). Para a amostra da janela isso
 * seria um loop: `drawHierarchy` tem fama de devolver branco em tela sã, e
 * como toda retomada saudável passa por `verify-ok`, uma sonda que mentisse
 * recarregaria o app EM TODA RETOMADA com o teto sempre cheio. Este contador
 * nunca é devolvido: uma sonda mentirosa custa no máximo UM reload por
 * abertura do app, e o diário mostra `native-screen-uniform` em retomada
 * sã — que é a calibração que decide se ela mente.
 */
export const SCREEN_RECOVERY_MAX_RELOADS = 1;
export const SCREEN_RECOVERY_KEY = "livra:screen_recovery_reloads";

export function claimScreenRecoverySlot(storage: Storage | null | undefined): boolean {
  if (!storage) return false;
  try {
    const raw = storage.getItem(SCREEN_RECOVERY_KEY);
    const n = raw === null ? 0 : Number.parseInt(raw, 10);
    const used = Number.isFinite(n) && n > 0 ? n : 0;
    if (used >= SCREEN_RECOVERY_MAX_RELOADS) return false;
    storage.setItem(SCREEN_RECOVERY_KEY, String(used + 1));
    return true;
  } catch {
    return false;
  }
}

export function readRecoveryCount(storage: Storage | null | undefined): number {
  try {
    const raw = storage?.getItem(FIRESTORE_RECOVERY_KEY);
    const n = raw === null || raw === undefined ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * Consome uma vaga de recuperação. `true` = pode recarregar AGORA.
 *
 * ⚠️ **Sem storage durável a resposta é `false`, e é de propósito.** O contador
 * precisa sobreviver ao próprio reload para o teto significar alguma coisa;
 * sem ele não há como distinguir a primeira tentativa da centésima, e a
 * recuperação vira o loop de boot que o teto existe para impedir. App travado
 * é ruim; app reiniciando sem parar é pior, e ainda apaga o rastro.
 *
 * Grava ANTES de recarregar — se a página sumir no meio, o gasto já está
 * contabilizado.
 */
export function claimRecoverySlot(
  storage: Storage | null | undefined,
  max: number = FIRESTORE_RECOVERY_MAX_RELOADS,
): boolean {
  if (!storage) return false;
  try {
    const used = readRecoveryCount(storage);
    if (used >= max) return false;
    storage.setItem(FIRESTORE_RECOVERY_KEY, String(used + 1));
    return true;
  } catch {
    return false;
  }
}

/**
 * Devolve as vagas. Chamado quando a sonda responde `alive`: uma abertura que
 * se recuperou e voltou a funcionar não pode carregar o contador gasto, senão
 * o próximo travamento REAL da mesma sessão já nasce sem direito a conserto.
 */
export function clearRecoveryCount(storage: Storage | null | undefined): void {
  try {
    storage?.removeItem(FIRESTORE_RECOVERY_KEY);
  } catch {
    // Storage indisponível: não havia contador para limpar.
  }
}
